import { useState } from 'react'
import {
  SPORT_CONFIGS,
  FIELD_GROUPS,
  getAvailableItemTypes,
  getItemTypeConfig,
} from '@/config/formConfig'
import type {
  SportType,
  Category,
  Condition
} from '@/config/formConfig'
import DynamicFieldGroup from './DynamicFieldGroup'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import MediaUploadZone from './MediaUploadZone'

interface ItemFormWizardProps {
  onSubmit: (data: any) => void
  loading?: boolean
  mediaUploadKey: number
  onMediaUploaded: (mediaIds: string[]) => void
}

const CATEGORIES: { label: string; value: Category; icon: string }[] = [
  { label: 'Clothing', value: 'CLOTHING', icon: '👕' },
  { label: 'Footwear', value: 'FOOTWEAR', icon: '👟' },
  { label: 'Equipment', value: 'EQUIPMENT', icon: '⚾' },
  { label: 'Accessories', value: 'ACCESSORIES', icon: '🧤' }
]

const CONDITIONS: { label: string; value: Condition }[] = [
  { label: 'Game Worn', value: 'GAME_WORN' },
  { label: 'Warmup Worn', value: 'WARMUP_WORN' },
  { label: 'Player Issued', value: 'PLAYER_ISSUED' },
  { label: 'Unused', value: 'UNUSED' },
  { label: 'Damaged', value: 'DAMAGED' }
]

export default function ItemFormWizard({ onSubmit, loading, mediaUploadKey, onMediaUploaded }: ItemFormWizardProps) {
  const [step, setStep] = useState(1)
  const [selectedSport, setSelectedSport] = useState<SportType | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [selectedItemType, setSelectedItemType] = useState<string | null>(null)
  const [formData, setFormData] = useState<Record<string, any>>({
    price: '0.01',
    condition: '',
    mediaIds: [],
    description: '',
    stats: {}
  })

  const sportConfig = selectedSport ? SPORT_CONFIGS[selectedSport] : null
  const itemTypeConfig = selectedSport && selectedCategory && selectedItemType
    ? getItemTypeConfig(selectedSport, selectedCategory, selectedItemType)
    : null
  const availableItemTypes = selectedSport && selectedCategory
    ? getAvailableItemTypes(selectedSport, selectedCategory)
    : []

  const handleFieldChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleStatChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      stats: { ...prev.stats, [name]: value }
    }))
  }

  const handleNext = () => {
    setStep(prev => prev + 1)
  }

  const handleBack = () => {
    setStep(prev => prev - 1)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Build the final listing request
    const finalData = buildListingRequest()
    onSubmit(finalData)
  }

  const buildListingRequest = () => {
    const userStr = localStorage.getItem('user')
    const user = userStr ? JSON.parse(userStr) : null
    const ownerId = user?.id || 'admin-user'

    // Build title
    let title = ''
    if (itemTypeConfig?.playerSpecific && formData.playerName) {
      title = `${formData.playerName}`
      if (formData.number) title += ` #${formData.number}`
      if (formData.team) title += ` ${formData.team}`
    } else if (formData.eventTitle) {
      title = formData.eventTitle
    } else if (formData.team) {
      title = `${formData.team} ${selectedCategory}`
    } else {
      title = `${selectedSport} ${selectedCategory}`
    }

    // Build description from all relevant fields
    const descriptionParts = [formData.description]

    if (formData.opponent) descriptionParts.push(`vs ${formData.opponent}`)
    if (formData.venue) descriptionParts.push(`at ${formData.venue}`)
    if (formData.eventDate) descriptionParts.push(`Date: ${formData.eventDate}`)

    // Add custom fields to description
    if (formData.significance) descriptionParts.push(`\n${formData.significance}`)
    if (formData.homeTeam && formData.awayTeam) {
      descriptionParts.push(`${formData.homeTeam} vs ${formData.awayTeam}`)
    }
    if (formData.finalScore) descriptionParts.push(`Score: ${formData.finalScore}`)

    // Add stats to description
    if (itemTypeConfig?.hasStats && itemTypeConfig.statFields) {
      const statStrings = itemTypeConfig.statFields
        .filter(stat => formData.stats[stat.name])
        .map(stat => `${formData.stats[stat.name]} ${stat.label}`)

      if (statStrings.length > 0) {
        descriptionParts.push(`\nStats: ${statStrings.join(', ')}`)
      }
    }

    const description = descriptionParts.filter(Boolean).join('\n')

    return {
      ownerId,
      title: title.trim(),
      description: description.trim(),
      price: parseFloat(formData.price) || 0.01,
      category: selectedCategory!,
      condition: formData.condition,
      sportType: selectedSport!,
      playerName: formData.playerName || undefined,
      teamOrClub: formData.team || undefined,
      leagueOrEvent: formData.eventTitle || formData.league || undefined,
      dateOrSeason: formData.eventDate || formData.season || undefined,
      size: formData.size || formData.shoeSize || undefined,
      mediaIds: formData.mediaIds.length > 0 ? formData.mediaIds : undefined
    }
  }

  // Step 1: Select Sport
  if (step === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold gradient-text mb-2">Select Sport</h3>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Choose the sport this item is associated with
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.values(SPORT_CONFIGS).map(sport => (
            <button
              key={sport.value}
              type="button"
              onClick={() => {
                setSelectedSport(sport.value)
                handleNext()
              }}
              className="glass-card shimmer transition-all duration-300 hover:scale-105"
              style={{
                padding: 'var(--spacing-xl)',
                textAlign: 'center',
                border: selectedSport === sport.value ? '2px solid var(--brand-primary)' : 'none'
              }}
            >
              <div className="text-4xl mb-3">{sport.icon}</div>
              <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {sport.label}
              </div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Step 2: Select Category
  if (step === 2) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold gradient-text mb-2">Select Category</h3>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            What type of item is this?
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map(category => (
            <button
              key={category.value}
              type="button"
              onClick={() => {
                setSelectedCategory(category.value)
                handleNext()
              }}
              className="glass-card shimmer transition-all duration-300 hover:scale-105"
              style={{
                padding: 'var(--spacing-xl)',
                textAlign: 'center'
              }}
            >
              <div className="text-4xl mb-3">{category.icon}</div>
              <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {category.label}
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-center mt-6">
          <Button type="button" onClick={handleBack} style={{ backgroundColor: 'var(--gray-600)' }}>
            ← Back
          </Button>
        </div>
      </div>
    )
  }

  // Step 3: Select Item Type (if multiple options exist)
  if (step === 3 && availableItemTypes.length > 1) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold gradient-text mb-2">Select Item Type</h3>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            What specific type of {selectedCategory?.toLowerCase()} is this?
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {availableItemTypes.map(itemType => (
            <button
              key={itemType.value}
              type="button"
              onClick={() => {
                setSelectedItemType(itemType.value)
                handleNext()
              }}
              className="glass-card shimmer transition-all duration-300 hover:scale-105"
              style={{
                padding: 'var(--spacing-lg)',
                textAlign: 'center'
              }}
            >
              <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                {itemType.label}
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-center mt-6">
          <Button type="button" onClick={handleBack} style={{ backgroundColor: 'var(--gray-600)' }}>
            ← Back
          </Button>
        </div>
      </div>
    )
  }

  // Auto-select if only one item type exists
  if (step === 3 && availableItemTypes.length === 1 && !selectedItemType) {
    setSelectedItemType(availableItemTypes[0].value)
    setStep(4)
    return null
  }

  // Step 4: Fill Form Fields
  if (step >= 3) {
    if (!itemTypeConfig) return null

    const customLabels: Record<string, string> = {}
    if (sportConfig?.numberLabel) {
      customLabels.number = sportConfig.numberLabel
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold gradient-text mb-2">
            {sportConfig?.icon} {sportConfig?.label} - {selectedCategory}
          </h3>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Fill in the details for this {itemTypeConfig.label.toLowerCase()}
          </p>
        </div>

        {/* Render field groups */}
        {itemTypeConfig.groups.map(groupName => {
          const group = FIELD_GROUPS[groupName]
          if (!group) return null
          return (
            <DynamicFieldGroup
              key={groupName}
              group={group}
              values={formData}
              onChange={handleFieldChange}
              customLabels={customLabels}
            />
          )
        })}

        {/* Render custom fields */}
        {itemTypeConfig.customFields && itemTypeConfig.customFields.length > 0 && (
          <div className="glass-card" style={{
            padding: 'var(--spacing-lg)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <h4 className="text-lg font-semibold gradient-text mb-4">Additional Details</h4>
            <div className="space-y-4">
              {itemTypeConfig.customFields.map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    {field.label}
                    {field.required && <span style={{ color: 'var(--accent-danger)' }}> *</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      name={field.name}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      required={field.required}
                      rows={4}
                      className="input"
                      style={{ resize: 'vertical', minHeight: '100px' }}
                    />
                  ) : (
                    <Input
                      name={field.name}
                      type={field.type}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats section */}
        {itemTypeConfig.hasStats && itemTypeConfig.statFields && (
          <div className="glass-card" style={{
            padding: 'var(--spacing-lg)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <h4 className="text-lg font-semibold" style={{ color: 'var(--accent-success)' }}>
              Performance Stats (Optional)
            </h4>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Add performance statistics from the event
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {itemTypeConfig.statFields.map(stat => (
                <Input
                  key={stat.name}
                  name={stat.name}
                  type={stat.type}
                  placeholder={stat.label}
                  value={formData.stats[stat.name] || ''}
                  onChange={(e) => handleStatChange(stat.name, e.target.value)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Core fields: Condition & Price */}
        <div className="glass-card" style={{
          padding: 'var(--spacing-lg)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <h4 className="text-lg font-semibold gradient-text mb-4">Item Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Condition <span style={{ color: 'var(--accent-danger)' }}>*</span>
              </label>
              <select
                name="condition"
                value={formData.condition}
                onChange={(e) => handleFieldChange('condition', e.target.value)}
                required
                className="input"
              >
                <option value="">Select Condition</option>
                {CONDITIONS.map(condition => (
                  <option key={condition.value} value={condition.value}>
                    {condition.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Estimated Price <span style={{ color: 'var(--accent-danger)' }}>*</span>
              </label>
              <Input
                name="price"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={formData.price}
                onChange={(e) => handleFieldChange('price', e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="glass-card" style={{
          padding: 'var(--spacing-lg)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <h4 className="text-lg font-semibold" style={{ color: 'var(--accent-warning)' }}>Description</h4>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Add any additional context, provenance, or special notes
          </p>
          <textarea
            name="description"
            placeholder="Describe the significance of this item..."
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            rows={4}
            className="input"
            style={{ resize: 'vertical', minHeight: '120px' }}
          />
        </div>

        {/* Media Upload */}
        <div className="glass-card" style={{
          padding: 'var(--spacing-lg)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <h4 className="text-lg font-semibold" style={{ color: '#8B5CF6' }}>Media Upload</h4>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Upload images and videos (required, max 8 files)
          </p>
          <MediaUploadZone
            key={mediaUploadKey}
            onMediaUploaded={(ids) => {
              handleFieldChange('mediaIds', ids)
              onMediaUploaded(ids)
            }}
            maxFiles={8}
          />
          {formData.mediaIds.length > 0 && (
            <div className="mt-4 p-3 rounded-lg" style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <p className="text-sm font-medium" style={{ color: 'var(--accent-success)' }}>
                ✓ {formData.mediaIds.length} file{formData.mediaIds.length === 1 ? '' : 's'} uploaded successfully
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            type="button"
            onClick={handleBack}
            style={{ backgroundColor: 'var(--gray-600)' }}
          >
            ← Back
          </Button>
          <Button
            type="submit"
            disabled={loading || formData.mediaIds.length === 0}
            className="flex-1"
          >
            {loading ? 'Creating...' : 'Create Listing'}
          </Button>
        </div>
      </form>
    )
  }

  return null
}
